import {
  faBriefcase,
  faCircleNodes,
  faClockRotateLeft,
  faCodeBranch,
  faCodeCompare,
  faDatabase,
  faDiagramNested,
  faICursor,
  faPlugCircleBolt,
  faRobot,
  faSliders,
  faTableLayout,
  type IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import type { QueryKey } from "@tanstack/react-query";

import {
  type DecisionHistoryRecordV2,
  historyDecisionQueryOptions,
} from "src/api/decisionHistoryV2/decisionHistoryQueries";
import type { MultiversionTestRunResults } from "src/api/endpoints";
import { flowOfVersionQueryOptions } from "src/api/flowVersionQueries";
import {
  multiversionTestRunQueryOptions,
  pickTestRunVersion,
} from "src/api/testRuns/multiversionTestRunQueries";
import { DecisionEnvironment } from "src/api/types";
import { datasetQueryOptions } from "src/datasets/api/queries";
import { isIcebergEnabledForWorkspace } from "src/hooks/useIsIcebergEnabled";
import { LeftPaneOptions, URLKeys } from "src/router/SearchParams";
import {
  getFlowVersionsUrl,
  getUrlToAgentVersionPage,
  getUrlToAgentVersionsPage,
  getUrlToAuthoringPage,
  getUrlToCaseQueuePage,
  getUrlToCaseTemplateVersionPage,
  getUrlToCaseTemplateVersionsPage,
  getUrlToTestRunResults,
  getWsUrl,
} from "src/router/urls";
import { graphStore } from "src/store/StoreProvider";
import type { EntityResolutionContext } from "src/unifiedCopilot/context/entityResolution";
import type {
  ContextualEntityDeeplinkContext,
  ContextualEntityDefinition,
  ContextualEntityType,
} from "src/unifiedCopilot/context/types";

/**
 * The copilot's resource vocabulary. Every type here registers exactly one
 * entry in `CONTEXTUAL_ENTITY_DEFINITIONS`.
 */
export const REFERENCE_CONTEXTUAL_ENTITY_TYPES = [
  "flow",
  "flow-version",
  "node",
  "dataset",
  "agent",
  "agent-version",
  "agent-tool",
  "flow-parameter",
  "decision",
  "testrun",
  "case-template",
  "case-template-version",
  "case-queue",
] as const;

export type ReferenceContextualEntityType =
  (typeof REFERENCE_CONTEXTUAL_ENTITY_TYPES)[number];

/**
 * What a query holds, plus a fetch when it holds nothing yet. An action is
 * called once while the pill renders, so the fetch it starts there is what
 * makes the later click synchronous. Nothing to read when the workspace names
 * no host to read it from.
 */
const readOrWarm = <TData>(
  query: { queryKey: QueryKey } | undefined,
  context: ContextualEntityDeeplinkContext,
): TData | undefined => {
  if (!query) {
    return undefined;
  }
  const cached = context.queryClient.getQueryData<TData>(query.queryKey);
  if (cached === undefined) {
    // The rejection is swallowed, not ignored: a warm nobody awaited would
    // otherwise surface as an unhandled promise error, and a click that needs
    // the data reports its own failure through the mention's toast.
    void context.queryClient.ensureQueryData(query).catch(() => undefined);
  }
  return cached;
};

/** The router test run behind a `testrun` ref */
const testRunQuery = (testRunId: string, context: EntityResolutionContext) =>
  context.workspace.base_url
    ? multiversionTestRunQueryOptions(context.workspace.base_url, testRunId)
    : undefined;

/** The decision record behind a `decision` ref */
const decisionQuery = (decisionId: string, context: EntityResolutionContext) =>
  context.workspace.base_url
    ? historyDecisionQueryOptions({
        baseUrl: context.workspace.base_url,
        decisionId,
        iceberg: isIcebergEnabledForWorkspace(context.workspace),
      })
    : undefined;

/**
 * Everything one contextual entity type contributes to the copilot, in one
 * place: icon, mention group label, optional resolver and deeplink. The
 * reference-entity vocabulary derives from this map — adding a type means
 * adding exactly one entry here.
 */
export const CONTEXTUAL_ENTITY_DEFINITIONS: Record<
  ReferenceContextualEntityType,
  ContextualEntityDefinition
> = {
  agent: {
    icon: faRobot,
    mentionGroupLabel: "Agents",
    typeLabel: "Agent",
    iconColor: "orange-100",
    deeplink: {
      action: (ref, context) => () =>
        context.navigate(
          getUrlToAgentVersionsPage(context.orgId, context.wsId, ref.id),
        ),
    },
  },
  "agent-version": {
    icon: faCodeBranch,
    mentionGroupLabel: "Agent versions",
    typeLabel: "Agent version",
    iconColor: "gray-200",
    parentType: "agent",
    deeplink: {
      action: (ref, context) => {
        const agentId = ref.parent?.type === "agent" ? ref.parent.id : null;
        if (!agentId) {
          return undefined;
        }
        return () =>
          context.navigate(
            getUrlToAgentVersionPage(
              context.orgId,
              context.wsId,
              agentId,
              ref.id,
            ),
          );
      },
    },
  },
  "agent-tool": {
    icon: faPlugCircleBolt,
    mentionGroupLabel: "Agent tools",
    typeLabel: "Tool",
    iconColor: "gray-200",
    // A tool id is only addressable within its agent-version; there is no
    // tool-level page, so the pill opens the version that defines it.
    parentType: "agent-version",
    deeplink: {
      action: (ref, context) => {
        const parent = ref.parent;
        if (parent?.type !== "agent-version") {
          return undefined;
        }
        const agentId =
          parent.parent?.type === "agent" ? parent.parent.id : null;
        if (!agentId) {
          return undefined;
        }
        return () =>
          context.navigate(
            getUrlToAgentVersionPage(
              context.orgId,
              context.wsId,
              agentId,
              parent.id,
            ),
          );
      },
    },
  },
  flow: {
    icon: faDiagramNested,
    mentionGroupLabel: "Decision Flows",
    typeLabel: "Flow",
    iconColor: "indigo-100",
    deeplink: {
      action: (ref, context) => () =>
        context.navigate(
          getFlowVersionsUrl(context.orgId, context.wsId, ref.id),
        ),
    },
  },
  "flow-version": {
    icon: faCodeBranch,
    mentionGroupLabel: "Flow versions",
    typeLabel: "Version",
    iconColor: "gray-200",
    resolve: async (id, context) => {
      // Exact lookup by version id (GET /flow_versions/{id}/flow): a warm
      // react-query cache answers without a request, a cold one is a single
      // fetch. Unknown or inaccessible versions reject with a 404.
      const flow = await context.queryClient.ensureQueryData(
        flowOfVersionQueryOptions(id),
      );
      return {
        type: "flow-version",
        id,
        parent: { type: "flow", id: flow.id },
      };
    },
    deeplink: {
      action: (ref, context) => {
        const flowId = ref.parent?.type === "flow" ? ref.parent.id : null;
        if (!flowId) {
          return undefined;
        }
        return () =>
          context.navigate(
            getUrlToAuthoringPage(context.orgId, context.wsId, flowId, ref.id),
          );
      },
    },
  },
  dataset: {
    icon: faDatabase,
    mentionGroupLabel: "Datasets",
    typeLabel: "Dataset",
    iconColor: "gray-200",
    resolve: async (id, context) => {
      const baseUrl = context.workspace.base_url;
      if (!baseUrl) {
        return null;
      }
      // GET /datasets/{id} on the workspace host names the owning flow.
      const dataset = await context.queryClient.ensureQueryData(
        datasetQueryOptions(baseUrl, id),
      );
      return {
        type: "dataset",
        id,
        parent: { type: "flow", id: dataset.flow_id },
      };
    },
    deeplink: {
      action: (ref, context) => {
        const flowId = ref.parent?.type === "flow" ? ref.parent.id : null;
        if (!flowId) {
          return undefined;
        }
        // Datasets belong to a flow, not a version: the flow route redirects
        // to the latest version's authoring page and preserves the query
        // params that open this dataset in the datasets pane.
        return () =>
          context.navigate({
            pathname: `${getWsUrl(context.orgId, context.wsId)}flow/${flowId}/`,
            search: `?${new URLSearchParams({
              [URLKeys.LeftPane]: LeftPaneOptions.Datasets,
              [URLKeys.EditDatasetId]: ref.id,
            })}`,
          });
      },
    },
  },
  node: {
    icon: faCircleNodes,
    mentionGroupLabel: "Nodes",
    typeLabel: "Node",
    iconColor: "gray-200",
    // A node id is only addressable within its flow-version.
    parentType: "flow-version",
    deeplink: {
      action: (ref, context) => {
        const parent = ref.parent;
        if (parent?.type !== "flow-version") {
          return undefined;
        }
        // The ?node= param is only read on mount (useSyncSelectedNodeToURL),
        // so an already-open version needs direct store selection. The
        // pathname guard matters: graphStore.versionId is never cleared on
        // unmount, and it also excludes revision/simulation subpages.
        if (
          graphStore.versionId === parent.id &&
          context.pathname.endsWith(`/version/${parent.id}`)
        ) {
          return () => graphStore.setSelectedNode(ref.id, false, true);
        }
        const flowId = parent.parent?.type === "flow" ? parent.parent.id : null;
        if (!flowId) {
          return undefined;
        }
        return () =>
          context.navigate(
            getUrlToAuthoringPage(
              context.orgId,
              context.wsId,
              flowId,
              parent.id,
              { selectedNodeId: ref.id },
            ),
          );
      },
    },
  },
  "flow-parameter": {
    icon: faSliders,
    mentionGroupLabel: "Parameters",
    typeLabel: "Parameter",
    iconColor: "gray-200",
    parentType: "flow-version",
    description:
      "Addressed by the parameter's name as shown in the Parameters pane " +
      "(for example payout_threshold), never by a UUID.",
    deeplink: {
      action: (ref, context) => {
        const parent = ref.parent;
        if (parent?.type !== "flow-version") {
          return undefined;
        }
        const flowId = parent.parent?.type === "flow" ? parent.parent.id : null;
        if (!flowId) {
          return undefined;
        }
        // Search-param driven: the same URL opens the pane in place on an
        // already-open version and from any other page.
        return () =>
          context.navigate(
            getUrlToAuthoringPage(
              context.orgId,
              context.wsId,
              flowId,
              parent.id,
              {
                leftPane: LeftPaneOptions.Parameters,
                parameterName: ref.id,
              },
            ),
          );
      },
    },
  },
  decision: {
    icon: faClockRotateLeft,
    mentionGroupLabel: "Decisions",
    typeLabel: "Decision",
    iconColor: "gray-200",
    resolve: async (id, context) => {
      const query = decisionQuery(id, context);
      if (!query) {
        return null;
      }
      // GET /decisions/{id} on the workspace history API: the record names
      // the version and flow that produced the decision.
      const decision = await context.queryClient.ensureQueryData(query);
      return {
        type: "decision",
        id,
        parent: {
          type: "flow-version",
          id: decision.flow.version_id,
          parent: { type: "flow", id: decision.flow.id },
        },
      };
    },
    deeplink: {
      action: (ref, context) => {
        // The pane refuses decisions from the other environment, so the link
        // names it. One expression warms the record and reads it, so the two
        // cannot drift; a click that beats the read omits the environment and
        // the pane's default applies. Reading needs only the id, so the warm
        // starts before the chain is checked — the resolver a click without a
        // chain falls back to reads this same entry.
        const query = decisionQuery(ref.id, context);
        const decision = readOrWarm<DecisionHistoryRecordV2>(query, context);
        const version = ref.parent?.type === "flow-version" ? ref.parent : null;
        const flowId =
          version?.parent?.type === "flow" ? version.parent.id : null;
        if (!version || !flowId || !query) {
          return undefined;
        }
        // The link stops at the pane: selecting the decision's output node
        // would open the output schema over it (`useOpenSchemaEditor` fires
        // for any input/output selection), so the node is left to the user.
        return () =>
          context.navigate(
            getUrlToAuthoringPage(
              context.orgId,
              context.wsId,
              flowId,
              version.id,
              {
                leftPane: LeftPaneOptions.DecisionHistory,
                decisionId: ref.id,
                ...(decision && {
                  decisionEnv: decision.is_sandbox
                    ? DecisionEnvironment.SANDBOX
                    : DecisionEnvironment.LIVE,
                }),
              },
            ),
          );
      },
    },
  },
  testrun: {
    icon: faCodeCompare,
    mentionGroupLabel: "Test runs",
    typeLabel: "Test run",
    iconColor: "gray-200",
    resolve: async (id, context) => {
      const query = testRunQuery(id, context);
      if (!query) {
        return null;
      }
      // GET /run/api/v1/test-runs/{id}: the run names its versions but not
      // their flow, so the chain walker completes that through the
      // flow-version resolver above.
      const run = await context.queryClient.ensureQueryData(query);
      const version = pickTestRunVersion(run);
      return version
        ? {
            type: "testrun",
            id,
            parent: { type: "flow-version", id: version.id },
          }
        : null;
    },
    deeplink: {
      action: (ref, context) => {
        // One entity, two destinations: only the run itself can say which, so
        // it is warmed and read in one expression.
        const query = testRunQuery(ref.id, context);
        const run = readOrWarm<MultiversionTestRunResults>(query, context);
        const version = ref.parent?.type === "flow-version" ? ref.parent : null;
        const flowId =
          version?.parent?.type === "flow" ? version.parent.id : null;
        if (!version || !flowId || !query) {
          return undefined;
        }
        // The comparison pane is only right for a run that has more than one
        // version, so a click that beats the read lands on the graph — where
        // a single-version run belongs, and the reading of it never fails.
        const landingVersionId =
          run === undefined
            ? version.id
            : (pickTestRunVersion(run, version.id)?.id ?? version.id);
        return () =>
          context.navigate(
            getUrlToTestRunResults(
              context.orgId,
              context.wsId,
              flowId,
              landingVersionId,
              {
                testRunId: ref.id,
                isMultiversion: (run?.flow_versions.length ?? 1) > 1,
              },
            ),
          );
      },
    },
  },
  "case-template": {
    icon: faTableLayout,
    mentionGroupLabel: "Case templates",
    typeLabel: "Case template",
    iconColor: "yellow-100",
    deeplink: {
      action: (ref, context) => () =>
        context.navigate(
          getUrlToCaseTemplateVersionsPage(context.orgId, context.wsId, ref.id),
        ),
    },
  },
  "case-template-version": {
    icon: faCodeBranch,
    mentionGroupLabel: "Case template versions",
    typeLabel: "Case template version",
    iconColor: "gray-200",
    // A version is only addressable under its template, and no read maps a
    // version id back to one, so the mention has to carry the template.
    parentType: "case-template",
    deeplink: {
      action: (ref, context) => {
        const templateId =
          ref.parent?.type === "case-template" ? ref.parent.id : null;
        if (!templateId) {
          return undefined;
        }
        return () =>
          context.navigate(
            getUrlToCaseTemplateVersionPage(
              context.orgId,
              context.wsId,
              templateId,
              ref.id,
            ),
          );
      },
    },
  },
  "case-queue": {
    icon: faBriefcase,
    mentionGroupLabel: "Case queues",
    typeLabel: "Case queue",
    iconColor: "yellow-100",
    deeplink: {
      action: (ref, context) => () =>
        context.navigate(
          getUrlToCaseQueuePage({
            orgId: context.orgId,
            wsId: context.wsId,
            queueId: ref.id,
          }),
        ),
    },
  },
};

export const isReferenceContextualEntityType = (
  type: string,
): type is ReferenceContextualEntityType =>
  type in CONTEXTUAL_ENTITY_DEFINITIONS;

const referenceEntityIcons = Object.fromEntries(
  Object.entries(CONTEXTUAL_ENTITY_DEFINITIONS).map(([type, definition]) => [
    type,
    definition.icon,
  ]),
) as Record<ReferenceContextualEntityType, IconDefinition>;

// Derived from the definitions so it cannot drift; inline entity types render
// but are never mentionable, so they only contribute an icon.
export const CONTEXTUAL_ENTITY_ICONS: Record<
  ContextualEntityType,
  IconDefinition
> = {
  ...referenceEntityIcons,
  selection: faICursor,
};

export const CONTEXTUAL_ENTITY_MENTION_GROUP_LABELS: Record<
  ReferenceContextualEntityType,
  string
> = Object.fromEntries(
  Object.entries(CONTEXTUAL_ENTITY_DEFINITIONS).map(([type, definition]) => [
    type,
    definition.mentionGroupLabel,
  ]),
) as Record<ReferenceContextualEntityType, string>;
